import i18n from '../../i18n';
import { externalTheme } from '@marketiq/marketiq-core-theme';
import {
  CssBaseline,
  Theme,
  ThemeProvider,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import Analytics from '@cbreone/core-services/dist/services/analytic-service';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import InteractiveSurveyNavigation from '../../components/InteractiveSurveyNavigation';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';
import routes from '../../routes/interactive.routes';
import { Property, Survey } from '../../types';
import '@marketiq/marketiq-core-theme/src/index.css';
import '@cbre-internal/mapping-component/dist/collection/components/mapping-component/mapping-component.css';
import CommentsPanel, { UndoText } from '../../components/CommentsPanel';
import { getComments, postComment } from '../../services/comments-service';
import useInterval from '../../hooks/useInterval';
import CommentsContext from '../../hooks/CommentsContext';

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
  },
  contentBox: {
    marginTop: 48,
    width: '100%',
    height: 'calc(100vh - 49px)',
  },
}));

const InteractiveApp: React.FC<Props> = () => {
  const classes = useStyles();
  const [survey, setSurvey] = useState<Survey>({} as Survey);
  const value = useMemo(
    () => ({ survey, setSurvey }),
    [survey],
  );
  const [commentPanelStatus, setCommentPanelStatus] = useState<boolean>(false);
  const [commPanelFormEnabled, setCommPanelFormEnabled] = useState<boolean>(true);
  const [surveyComments, setSurveyComments] = useState<any>([]);
  const [commentPending, setCommentPending] = useState<boolean>(false);
  const [commPropId, setCommPropId] = useState<string | undefined>('');
  const [comment, setComment] = useState<string>('');
  const [user, setUser] = useState(localStorage.getItem('displayName') || '');
  const [propsWithComments, setPropsWithComments] = useState<any>([]);
  const [cancelable, setCancelable] = React.useState(false);
  const [undo, setUndo] = React.useState(false);
  const waitTime = 5;
  const [timeLeft, setTimeLeft] = React.useState(waitTime);
  const [isClientCommentEnabled, setIsClientCommentEnabled] = React.useState(false);
  const [postResponse, setPostResponse] = useState<any>();
  const [undoComment, setUndoComment] = useState<UndoText>({ comment: '',
    isNew: false,
    propertyId: '',
    surveyId: '',
    userName: '' });

  const getUniqueProperties = (data: any[]) => {
    const unique = [...new Set(data.map((item: Property) => item.propertyId))];
    return unique;
  };

  const getCurrentComments = () => new Promise((resolve, reject) => {
    if (survey.isClientCommentEnabled !== undefined) setIsClientCommentEnabled(survey?.isClientCommentEnabled);
    if (survey.id !== undefined) {
      Promise.all([getComments(survey?.id)]).then((data) => {
        setSurveyComments(data[0]);
        setPropsWithComments(getUniqueProperties(data[0]));
        resolve({ data });
      }).catch((error) => {
        reject();
      });
    }
  });

  useEffect(() => {
    if (survey.id !== undefined) {
      getCurrentComments();
    }
  }, [survey]);
  const getCommentPropData = (propId: string) => {
    const prop = survey.properties.find((p: any) => propId === p.id);
    const propData = {
      address: prop?.address1,
      coordinates: `${prop?.latitude}, ${prop?.longitude}`,
      availableSF: prop?.customFields?.[9].value,
      propertyName: prop?.name,
    };
    return propData;
  };

  const postMyComment = () => {
    setCancelable(false);
    setCommPanelFormEnabled(true);
    setTimeLeft(waitTime);
    if (!undo && commentPending) {
      setCommentPending(false);
      const commentData = {
        surveyId: survey.id,
        propertyId: commPropId,
        userName: user,
        comment,
      };
      postComment(survey.id, commentData, setPostResponse);
      if (commPropId) {
        const prop = getCommentPropData(commPropId);
        let internal = false;
        if (user) internal = true;
        const commData = {
          event: 'commentedProperty',
          survey_id: survey?.id,
          project_id: survey.projectId,
          address: prop.address,
          coordinates: prop.coordinates,
          available_sf: prop.availableSF,
          property_name: prop.propertyName,
          date: Date(),
          internal,
          comment
        };
        Analytics.send(commData);
      } else {
        console.log('no commPropId')
      }

      setPropsWithComments([...propsWithComments, commPropId]);
      const clonedSurveyComments = Object.assign([], surveyComments);
      clonedSurveyComments.pop();
      const newComment = {
        surveyId: survey?.id,
        propertyId: commPropId,
        userName: user,
        comment,
        isNew: false,
      };
      const updatedComments = [...clonedSurveyComments, newComment];
      setSurveyComments(updatedComments);
      if (commPropId) {
        const oldDraft = localStorage.getItem(commPropId);
        if (oldDraft != null) {
          localStorage.removeItem(commPropId)
        } 
      }
    }
  };

  useInterval(() => {
    setCommentPending(true);
    setTimeLeft(timeLeft - 1);
    if (timeLeft <= 0) {
      postMyComment();
    }
  }, cancelable ? 1000 : null)

  const editSurveyComments = (
    propId: string | undefined,
    comm: string | undefined,
  ) => {
    setUndo(false); 
    if (comm === undefined && propId === undefined) {
      // remove last pending comment
      const clonedSurveyComments = Object.assign([], surveyComments);
      const commentValue = clonedSurveyComments.pop();
      setUndoComment(commentValue);
      setUndo(true);
      setCancelable(false);
      setSurveyComments(clonedSurveyComments);
    } else if (propId === undefined && comm) {
      // add pending survey comment
      const clonedSurveyComments = Object.assign([], surveyComments);
      clonedSurveyComments[clonedSurveyComments.length - 1].isNew = false;
      setSurveyComments(clonedSurveyComments);
    } else {
      // add pending property comment
      setCommPropId(propId || '');
      setComment(comm || '');
      setCancelable(true);
      const newComment = {
        surveyId: survey?.id,
        propertyId: propId,
        userName: user,
        comment: comm,
        isNew: true,
      };
      const updatedComments = [...(surveyComments || []), newComment];
      setSurveyComments(updatedComments);
    }
  }; 
  const commentValue = useMemo(
    () => ({
      commentPanelStatus,
      setCommentPanelStatus,
      commPanelFormEnabled,
      setCommPanelFormEnabled,
      commPropId: commPropId || '',
      setCommPropId,
      user,
      setUser,
      propsWithComments,
      setPropsWithComments,
      commentPending,
      setCommentPending,
      timeLeft,
      setTimeLeft,
      isClientCommentEnabled,
      setIsClientCommentEnabled,
    }), [
      commentPanelStatus,
      commPanelFormEnabled,
      commPropId,
      user,
      propsWithComments,
      commentPending,
      timeLeft,
      isClientCommentEnabled
    ],
  );
  useEffect(() => {
    if (postResponse !== undefined) {
      const latestResponse = postResponse.pop();
      const newComment = {
        surveyId: survey?.id,
        propertyId: latestResponse.propertyId,
        userName: user,
        comment: latestResponse.comment,
        isNew: false,
        createdOn: latestResponse.createdOn,
      };
      const updatedComments = [...surveyComments.slice(0, -1), newComment];
      setSurveyComments(updatedComments);
    }
  }, [postResponse]);
  return (
  <ThemeProvider theme={externalTheme}>
    <CommentsContext.Provider value={commentValue}>
      <BrowserRouter>
        <Suspense fallback={<div>{i18n.t('common.loading')}</div>}>
          <Switch>
            {routes.map(({ path, exact, component: Component }) => (
              <Route
                path={path}
                exact={exact}
                render={(props) => {
                  if (path === '/:surveyId/login') {
                    return <Component {...props} />;
                  }
                  if (path === '/:surveyId/login/:passcode') {
                    return <Component {...props} />;
                  }
                  return (
                      <div className={classes.root}>
                        <CssBaseline />
                        <InteractiveSurveyContext.Provider value={value}>
                          <InteractiveSurveyNavigation {...props}/>
                          <div className={classes.contentBox}>
                          <Component {...props} />
                              {
                                isClientCommentEnabled && (
                                  <CommentsPanel
                                    onDismiss={() => { setCommentPanelStatus(false) }}
                                    commentArray={surveyComments}
                                    editSurveyComments={editSurveyComments}
                                    returnUndoText={undoComment}
                                    onClose={postMyComment}
                                  />
                                ) 
                              }
                          </div>
                        </InteractiveSurveyContext.Provider>
                      </div>
                  );
                }}
                key={path}
              />
            ))}
          </Switch>
        </Suspense>
        </BrowserRouter>
    </CommentsContext.Provider>
  </ThemeProvider>
  );
};

export type Props = {};
InteractiveApp.displayName = 'InteractiveApp';
export default InteractiveApp;
